import { Sparkles } from 'lucide-react';
import { motion, type Transition, useReducedMotion } from 'motion/react';
import { Link, useLocation } from 'react-router';
import torusAlpha from '@/assets/brand/torus-alpha.webp';
import { GrainOverlay } from '@/components/brand/grain-overlay';
import { LongArrow } from '@/components/brand/long-arrow';
import { APP_CARD_BACKGROUND } from '@/components/home/app-card-palette';
import {
  NewsDot,
  useHasUnseenEdition,
} from '@/components/whats-new/whats-new-nav-item';
import { useWhatsNew } from '@/components/whats-new/whats-new-provider';
import { useTenant } from '@/hooks/use-tenant';
import { useI18nContext } from '@/i18n/i18n-react';
import { isNavItemActive, whatsNewHref } from '@/lib/navigation';
import { EDITIONS } from '@/lib/whats-new/registry';
import { cn } from '@/lib/utils';

/** The still torus floats ±5px every 9 s and never spins (NEWS-46). */
const FLOAT_Y = [0, -5, 0];
const FLOAT_TRANSITION: Transition = {
  duration: 9,
  ease: 'easeInOut',
  repeat: Infinity,
};

/**
 * "Novidades chegaram" (NEWS-46/49): the newest edition, unseen, as a small
 * navy card that plays it: above the sidebar's "Novidades" (expanded
 * sidebar, not on the What's new page) or at the top of the mobile Início,
 * with the transparent torus still floating at the right, off the text.
 */
export function WhatsNewTeaser({
  variant,
}: {
  variant: 'sidebar' | 'home-mobile';
}) {
  const { LL } = useI18nContext();
  const { latest, open } = useWhatsNew();
  const tenant = useTenant();
  const pathname = useLocation().pathname;
  const unseen = useHasUnseenEdition();
  const reduceMotion = useReducedMotion() ?? false;

  if (!unseen || latest === null) return null;
  if (
    variant === 'sidebar' &&
    isNavItemActive(whatsNewHref(tenant.slug), pathname)
  ) {
    return null;
  }

  const count = Object.prototype.hasOwnProperty.call(EDITIONS, latest.id)
    ? EDITIONS[latest.id].archive.reduce(
        (sum, block) => sum + block.items.length,
        0
      )
    : 0;
  const sidebar = variant === 'sidebar';

  return (
    <button
      type="button"
      data-testid="whats-new-teaser"
      onClick={() => open(latest.id)}
      className={cn(
        'group relative isolate block w-full overflow-hidden rounded-xl text-left text-white shadow-[0_14px_30px_-18px_rgba(11,52,140,0.8)] outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        sidebar
          ? 'mb-1.5 group-data-[collapsible=icon]:hidden'
          : 'rounded-mobile-card'
      )}
    >
      <span
        aria-hidden="true"
        className="absolute inset-0 -z-20"
        style={{ background: APP_CARD_BACKGROUND }}
      />
      <GrainOverlay className="-z-10" />
      <motion.img
        src={torusAlpha}
        alt=""
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute -z-10 select-none',
          sidebar
            ? '-right-6 -top-6 w-[98px]'
            : '-right-[18px] top-1/2 -mt-[60px] w-[120px]'
        )}
        animate={reduceMotion ? undefined : { y: FLOAT_Y }}
        transition={reduceMotion ? undefined : FLOAT_TRANSITION}
      />
      <span
        className={cn(
          'flex flex-col',
          sidebar
            ? 'gap-1.5 p-3.5 pb-[13px]'
            : 'gap-[5px] py-3.5 pl-4 pr-[120px]'
        )}
      >
        <span
          data-teaser-text
          className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/[.86]"
        >
          {LL.whatsNew.teaser.eyebrow()}
        </span>
        <span
          data-teaser-text
          className={cn(
            'font-semibold leading-[1.25] tracking-[-0.01em]',
            sidebar ? 'max-w-[140px] text-sm' : 'text-[15px]'
          )}
        >
          {LL.whatsNew.teaser.title({ count, store: tenant.name })}
        </span>
        <span
          data-teaser-text
          className={cn(
            'mt-0.5 flex items-center gap-1.5 self-start font-semibold text-white/[.86]',
            sidebar ? 'text-xs' : 'text-[12.5px]'
          )}
        >
          {LL.whatsNew.teaser.watch()}
          <LongArrow lineClassName="w-7 group-hover:w-10" />
        </span>
      </span>
    </button>
  );
}

/**
 * ✦ "Novidades" in the mobile Início header (NEWS-47): 44px, to the What's
 * new page, with the blue dot while the newest edition is unseen.
 */
export function WhatsNewHeaderButton() {
  const { LL } = useI18nContext();
  const tenant = useTenant();
  const unseen = useHasUnseenEdition();
  return (
    <Link
      to={whatsNewHref(tenant.slug)}
      aria-label={LL.whatsNew.nav.label()}
      className="relative grid size-11 place-items-center rounded-[10px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Sparkles aria-hidden="true" className="size-5" strokeWidth={1.6} />
      {unseen && (
        <NewsDot className="absolute right-[9px] top-2.5 size-2 rounded-full ring-2 ring-background" />
      )}
    </Link>
  );
}
